
import React, { createContext, useContext, useEffect, useState } from 'react';
import { getConnectionInfo, getNetworkErrorMessage } from '@/utils/networkUtils';

interface NetworkContextType {
  online: boolean;
  connectionInfo: {
    type: string;
    downlinkMbps: number;
    rtt: number;
    saveData: boolean;
  };
  lastError: string | null;
  clearError: () => void;
  registerError: (error: any) => void;
}

const NetworkContext = createContext<NetworkContextType | undefined>(undefined);

export const NetworkProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [online, setOnline] = useState(navigator.onLine);
  const [connectionInfo, setConnectionInfo] = useState(getConnectionInfo());
  const [lastError, setLastError] = useState<string | null>(null);

  useEffect(() => {
    const handleOnline = () => setOnline(true);
    const handleOffline = () => setOnline(false);
    
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    
    // Update connection info if supported
    const connection = (navigator as any).connection;
    if (connection) {
      const updateConnectionInfo = () => setConnectionInfo(getConnectionInfo());
      connection.addEventListener('change', updateConnectionInfo);
      return () => {
        connection.removeEventListener('change', updateConnectionInfo);
        window.removeEventListener('online', handleOnline);
        window.removeEventListener('offline', handleOffline);
      };
    }
    
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);
  
  const clearError = () => setLastError(null);
  
  const registerError = (error: any) => {
    const errorMessage = getNetworkErrorMessage(error);
    console.log('Network error registered:', errorMessage);
    setLastError(errorMessage);
    
    // Auto-clear error after 10 seconds
    setTimeout(clearError, 10000);
  };
  
  return (
    <NetworkContext.Provider 
      value={{ 
        online, 
        connectionInfo, 
        lastError, 
        clearError,
        registerError
      }}
    >
      {children}
    </NetworkContext.Provider>
  );
};

export const useNetwork = () => {
  const context = useContext(NetworkContext);
  if (context === undefined) {
    throw new Error('useNetwork must be used within a NetworkProvider');
  }
  return context;
};
